import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { alert } from "devextreme/ui/dialog";
import { MESSAGES } from "../../../../../common/Messages";
import { readQty } from "./ImItemStatusLayout";

function fmtQty(value) {
    if (value === null || value === undefined) return "0.00";
    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function SelectionRow({ label, value }) {
    const display = value || "\u2014";
    return (
        <div style={styles.selectionRow}>
            <span style={styles.selectionLabel}>{label}</span>
            <span style={styles.selectionValue}>{display}</span>
        </div>
    );
}

function QtyRow({ label, value, bold }) {
    const num = value ?? 0;
    const isNegative = typeof num === "number" && num < 0;
    return (
        <div style={styles.qtyRow}>
            <span
                style={{
                    ...styles.qtyLabel,
                    fontWeight: bold ? "bold" : "normal",
                }}
            >
                {label}
            </span>
            <span
                style={{
                    ...styles.qtyValue,
                    fontWeight: bold ? "bold" : "normal",
                    color: isNegative ? window._env_.NegativeValueColor : undefined,
                }}
            >
                {fmtQty(num)}
            </span>
        </div>
    );
}

function ImItemStatusPopup(props) {
    const { showPopup = {}, setShowPopup, oidc } = props;
    const visible = showPopup.visible === true;
    const accessToken = oidc?.user?.access_token;

    const [loading, setLoading] = useState(false);
    const [quantities, setQuantities] = useState(null);

    const loadData = useCallback(async () => {
        if (!showPopup.rItem || !accessToken) return;
        setLoading(true);
        try {
            const params = { rItem: showPopup.rItem };
            const lotId = showPopup.rLot;
            params.rLot =
                lotId && lotId !== "00000000-0000-0000-0000-000000000000"
                    ? lotId
                    : "00000000-0000-0000-0000-000000000000";
            const res = await axios.get(
                `${window._env_.REACT_APP_APIS}/${routes.OM.GetImItemStatusQuantitySummary}`,
                {
                    params,
                    headers: { Authorization: `Bearer ${accessToken}` },
                }
            );
            setQuantities(res.data || {});
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                "Failed to load item status.";
            alert(msg, MESSAGES.ERROR);
            setQuantities({});
        } finally {
            setLoading(false);
        }
    }, [showPopup.rItem, showPopup.rLot, accessToken]);

    useEffect(() => {
        if (!visible) return;
        setQuantities(null);
        loadData();
    }, [visible, loadData]);

    const handleHiding = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    if (!visible) return null;

    const q = quantities;
    const n = (pascal) => readQty(q, pascal);
    const inUn = n("InUnavailable") ?? 0;
    const outUn = n("OutUnavailable") ?? 0;
    const unavailable = inUn + outUn;

    return (
        <Popup
            visible={visible}
            onHiding={handleHiding}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title="IM Item Status"
            width={720}
            height="auto"
            maxHeight="90vh"
            container="#root"
        >
            <div style={styles.popupContent}>
                {loading && (
                    <div style={styles.loadingBar}>Loading…</div>
                )}
                <div style={styles.columnsContainer}>
                    {/* Selection */}
                    <fieldset style={styles.fieldsetLeft}>
                        <legend style={styles.legend}>Selection</legend>
                        <SelectionRow label="Item" value={showPopup.itemCode} />
                        <SelectionRow label="Warehouse" value={showPopup.warehouseName} />
                        <SelectionRow label="Lot" value={showPopup.lotCode} />
                        <SelectionRow label="Sublot" value={showPopup.sublotCode} />
                        <SelectionRow label="Unit" value={showPopup.unitOfMeasure} />
                    </fieldset>

                    {/* Quantity Status */}
                    <fieldset style={styles.fieldsetRight}>
                        <legend style={styles.legend}>Quantity Status</legend>
                        {!loading && q && (
                            <>
                                <QtyRow label="On Hand" value={n("OnHand")} />
                                <QtyRow label="In Receiving" value={n("InReceiving")} />
                                <QtyRow label="In Shipping" value={n("OutShipped")} />
                                <QtyRow label="Unavailable" value={unavailable} />
                                <QtyRow label="Available" value={n("Available")} />
                                <QtyRow label="Pending Rec" value={n("InOrdered")} />
                                <QtyRow label="Pending Ship" value={n("OutOrdered")} />
                                <QtyRow label="BO Receiving" value={n("InBackorder")} />
                                <QtyRow label="BO Shipping" value={n("OutBackorder")} />
                                <QtyRow label="Net Available" value={n("NetAvailable")} bold />
                                <QtyRow label="Future Rec" value={n("InFuture")} />
                                <QtyRow label="Future Ship" value={n("OutFuture")} />
                            </>
                        )}
                    </fieldset>
                </div>

                <div style={styles.footer}>
                    <Button
                        text="OK"
                        type="default"
                        onClick={handleHiding}
                        width={120}
                    />
                </div>
            </div>
        </Popup>
    );
}

const styles = {
    popupContent: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
        padding: "16px",
    },
    loadingBar: {
        padding: "8px 0",
        color: "#666",
    },
    columnsContainer: {
        display: "flex",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        alignItems: "flex-start",
    },
    fieldsetLeft: {
        flex: "0 0 38%",
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "12px 16px",
        margin: 0,
    },
    fieldsetRight: {
        flex: 1,
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "12px 16px",
        margin: 0,
    },
    legend: {
        fontWeight: "bold",
        fontSize: "14px",
        padding: "0 6px",
    },
    selectionRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "5px 0",
        borderBottom: "1px solid #f0f0f0",
    },
    selectionLabel: {
        fontSize: "13px",
    },
    selectionValue: {
        fontWeight: 500,
        fontSize: "13px",
        textAlign: "right",
    },
    qtyRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "5px 0",
        borderBottom: "1px solid #f0f0f0",
    },
    qtyLabel: {
        fontSize: "13px",
    },
    qtyValue: {
        fontSize: "13px",
        textAlign: "right",
    },
    footer: {
        flexShrink: 0,
        padding: "16px 0 0",
        textAlign: "center",
    },
};

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(ImItemStatusPopup);
